import { useRef, useState } from "react";
import { IconLink, IconPhoto } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../components/app/AppToast";
import BottomPanel from "../../components/app/panels/BottomPanel";
import { Button } from "../../components/ui/Button";
import { shareImage } from "../../domains/app/utils/shareImage";
import { shareUrl } from "../../domains/app/utils/shareUrl";
import {
  gaEventVisualAbstractImageShared,
  gaEventVisualAbstractShared,
} from "../../tools/analytics/domains/article.analytics";
import { apiClient, type Api } from "../../services/api";
import { VisualAbstract, VisualAbstractThumbnail } from "./VisualAbstract";
import { VisualAbstractBlockerNotice } from "./VisualAbstractBlockerNotice";

type ReadyVisualAbstract = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "ready" }
>;

/**
 * The PNG is snapshotted from an off-screen copy pinned to this width, never
 * from the on-screen node: an export must not change shape with the device that
 * produced it. 480px stays under the poster's `@lg` container breakpoint
 * (32rem), so the exported image keeps the single-column shape the user just
 * saw in the preview.
 */
const EXPORT_WIDTH_PX = 480;

/** Renders 480px wide at 3x into a 1440px PNG — sharp on retina and in slides. */
const EXPORT_SCALE = 3;

type ShareState =
  { kind: "idle" } | { kind: "sharing"; channel: "public-link" | "image" };

export type UseShareVisualAbstract = {
  triggerShare: () => void;
  renderShareSheet: () => React.JSX.Element;
  /**
   * The desktop viewer's panel actions. It has no share sheet: the link goes to
   * the clipboard and the image to a download, where the phone hands both to
   * the OS sheet. Both stay behind the same blocker gate as the sheet.
   */
  desktop: {
    state: ShareState;
    isPublishable: boolean;
    copyLink: () => Promise<void>;
    downloadImage: () => Promise<void>;
    /** The off-screen poster the image is snapshotted from. */
    renderExportSurface: () => React.JSX.Element;
  };
};

export function useShareVisualAbstract(
  artifact: ReadyVisualAbstract
): UseShareVisualAbstract {
  const { t } = useTranslation();
  const exportRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<ShareState>({ kind: "idle" });

  const view = artifact.view;
  // Every blocker gates BOTH channels, not just the public link.
  // `figure_rights` and `patient_privacy` describe what may leave Juisci at
  // all — a PNG carries the same payload as the link, and being rendered
  // client-side it is the one channel the API cannot refuse. So the gate has to
  // live here.
  const isPublishable = artifact.blockers.length === 0;

  // Idempotent: returns the existing token when the artifact is already
  // shared, mints one otherwise — so nothing here needs to branch on
  // `publicUrl` beyond telling a first publication apart for analytics. Null
  // when the request fails; the caller says so in its own words.
  const publish = async (
    channel: "share_sheet" | "copy_link"
  ): Promise<string | null> => {
    let response;
    try {
      response = await apiClient.post(
        "/webapp/private/visual-abstracts/:id/share",
        { param: { id: artifact.id } }
      );
    } catch {
      return null;
    }
    if (artifact.publicUrl === null)
      gaEventVisualAbstractShared({
        visualAbstractId: artifact.id,
        roomId: artifact.roomId,
        language: artifact.language,
        family: artifact.family,
        sourceType: artifact.sourceType,
        generatorVersion: artifact.generatorVersion,
        channel,
      });
    return response.body.publicUrl;
  };

  const sharePublicLink = async (): Promise<void> => {
    setState({ kind: "sharing", channel: "public-link" });
    try {
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = publish("share_sheet").then((publicUrl) => {
        if (publicUrl === null)
          throw new Error(t("Unable to share the visual abstract"));
        return publicUrl;
      });
      // `title` and `text` must differ: targets that render both (Mail, Notes)
      // print them on consecutive lines, so passing the short title twice shows
      // it twice. The title has to stay in `text` — WhatsApp & co read only that
      // field, and /v/:token has no OG route, so the link carries no preview of
      // its own to name the artifact.
      await shareUrl(t("Visual abstract"), view.header.shortTitle, link);
      setIsOpen(false);
    } catch (error) {
      displayToast(
        error instanceof Error
          ? error.message
          : t("Unable to share the visual abstract"),
        "error"
      );
    } finally {
      setState({ kind: "idle" });
    }
  };

  const filename = `${view.articleSlug}-visual-abstract.png`;

  const snapshotPng = async (): Promise<Blob> => {
    const surface = exportRef.current;
    if (surface === null)
      throw new Error(
        "should not happen: the export surface is not mounted on the page"
      );
    const { domToBlob } = await import("modern-screenshot");
    // Web fonts land after first paint; snapshotting earlier bakes the
    // fallback system font into the PNG.
    await document.fonts.ready;
    return domToBlob(surface, { scale: EXPORT_SCALE, type: "image/png" });
  };

  const trackImageShared = (): void => {
    gaEventVisualAbstractImageShared({
      visualAbstractId: artifact.id,
      roomId: artifact.roomId,
      language: artifact.language,
      family: artifact.family,
      sourceType: artifact.sourceType,
      generatorVersion: artifact.generatorVersion,
    });
  };

  const sharePng = async (): Promise<void> => {
    setState({ kind: "sharing", channel: "image" });
    try {
      const blob = await snapshotPng();
      const file = new File([blob], filename, { type: "image/png" });

      // Web Share level 2 hands the file straight to the OS sheet (Photos,
      // AirDrop, Mail, …). Android's WebView ships no navigator.share at all,
      // so there the native bridge inside shareImage stays the only path.
      if (
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({ files: [file], title: view.header.shortTitle });
      } else if (
        // shareImage reports its own failure with a toast and returns false,
        // so a failed save must not count as a shared image.
        !(await shareImage(blob, filename, t("Visual abstract downloaded")))
      ) {
        return;
      }

      trackImageShared();
      setIsOpen(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("Couldn't share the visual abstract image.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setState({ kind: "idle" });
    }
  };

  const triggerShare = (): void => {
    setIsOpen(true);
  };

  const copyLink = async (): Promise<void> => {
    setState({ kind: "sharing", channel: "public-link" });
    try {
      const publicUrl = await publish("copy_link");
      if (publicUrl === null) {
        displayToast(t("Unable to share the visual abstract"), "error");
        return;
      }
      await navigator.clipboard.writeText(publicUrl);
      displayToast(t("Link copied"), "success");
    } catch (error) {
      console.error("Couldn't copy the visual abstract link.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setState({ kind: "idle" });
    }
  };

  // A download, never the OS share sheet: desktop browsers that implement
  // Web Share open a sheet the reader did not ask for.
  const downloadImage = async (): Promise<void> => {
    setState({ kind: "sharing", channel: "image" });
    try {
      const blob = await snapshotPng();
      // shareImage reports its own failure with a toast and returns false.
      if (await shareImage(blob, filename, t("Visual abstract downloaded")))
        trackImageShared();
    } catch (error) {
      console.error("Couldn't download the visual abstract image.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setState({ kind: "idle" });
    }
  };

  const renderExportSurface = (): React.JSX.Element => (
    <>
      {/* Off-screen rather than `hidden`: the browser must still lay this
          subtree out for the snapshot to have anything to read. `aria-hidden`
          keeps the duplicated poster (and its <main>) out of the a11y tree.

          Two nodes on purpose. modern-screenshot copies the computed style of
          the node it is handed onto its clone, so a snapshot root carrying
          `fixed` + `translateX` re-applies both inside the SVG foreignObject
          and comes back with the text dropped and the layout shoved
          off-canvas. The anchor keeps the positioning; the snapshotted node
          owns nothing but the export width. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          transform: "translateX(-200vw)",
          pointerEvents: "none",
        }}
      >
        <div ref={exportRef} style={{ width: `${EXPORT_WIDTH_PX}px` }}>
          {/* No usefulness prompt: this subtree IS the exported PNG, and a
              feedback control belongs to the screen it was asked on, not to the
              image that leaves the product. */}
          <VisualAbstract
            actions={{ kind: "preview", onUsefulness: null }}
            view={view}
          />
        </div>
      </div>
    </>
  );

  const renderShareSheet = (): React.JSX.Element => (
    <>
      <BottomPanel
        // The two share buttons live in `actions`, which BottomPanel pins below
        // the scroll area: the poster teaser is tall enough to push them past
        // the panel's 80dvh on a small phone otherwise.
        actions={
          isPublishable ? (
            <>
              <Button
                disabled={state.kind === "sharing"}
                leftIcon={<IconLink />}
                onClick={() => {
                  void sharePublicLink();
                }}
                size="md"
                variant="primary"
              >
                {state.kind === "sharing" && state.channel === "public-link"
                  ? t("Preparing the link…")
                  : t("Share public link")}
              </Button>
              <Button
                disabled={state.kind === "sharing"}
                leftIcon={<IconPhoto />}
                onClick={() => {
                  void sharePng();
                }}
                size="md"
                variant="outline"
              >
                {state.kind === "sharing" && state.channel === "image"
                  ? t("Preparing the image…")
                  : t("Share as image")}
              </Button>
            </>
          ) : null
        }
        displayCloseButton
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={t("Share")}
      >
        <div className="flex flex-col gap-4 px-5 pt-4">
          {/* The teaser runs ~800px tall — taller than the panel will ever be.
              Crop it to a deliberate preview and fade the cut, so it reads as a
              thumbnail rather than a poster that got truncated. It already
              carries the short title, so no title line is repeated below it. */}
          <div
            className="max-h-80 overflow-hidden"
            style={{
              maskImage: "linear-gradient(to bottom, black 78%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 78%, transparent)",
            }}
          >
            <VisualAbstractThumbnail href={null} view={view} />
          </div>

          {isPublishable ? null : (
            <VisualAbstractBlockerNotice blockers={artifact.blockers} />
          )}
        </div>
      </BottomPanel>

      {renderExportSurface()}
    </>
  );

  return {
    triggerShare,
    renderShareSheet,
    desktop: {
      state,
      isPublishable,
      copyLink,
      downloadImage,
      renderExportSurface,
    },
  };
}
